

import { useRouter } from '@/lib/navigation';
import { CheckCircle, Plus, ArrowRight } from "lucide-react";
import Badge from "../../components/Badge";
import { Task } from "./types";

interface TaskListProps {
  tasks: Task[];
  onAddTask?: () => void;
}

export default function TaskList({ tasks, onAddTask }: TaskListProps) {
  const router = useRouter();
  
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const progressPercent = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-violet-50 to-violet-100/50 border-b border-violet-100 flex items-center justify-between">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-violet-600" />
          Tasks
          <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-medium">
            {completedTasks}/{tasks.length}
          </span>
        </h3>
        {onAddTask && (
          <button
            onClick={onAddTask}
            className="text-xs px-2 py-1 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>
      
      {/* Progress Bar */}
      {tasks.length > 0 && (
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-100">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-stone-500">Progress</span>
            <span className="font-semibold text-violet-600">{progressPercent}%</span>
          </div>
          <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
      
      <div className="divide-y divide-stone-100">
        {tasks.length > 0 ? (
          tasks.slice(0, 5).map((task) => (
            <div key={task.id} className="p-3 flex items-center gap-3 hover:bg-stone-50 transition-colors">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                task.status === 'completed' ? 'bg-emerald-400' : 
                task.status === 'in_progress' ? 'bg-blue-400' : 'bg-amber-400'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-stone-700 truncate">{task.title}</p>
                {task.assigned_to && (
                  <p className="text-xs text-stone-400">{task.assigned_to}</p>
                )}
              </div>
              <Badge label={task.status.replace('_', ' ')} status={task.status} />
            </div>
          ))
        ) : (
          <div className="p-6 text-center">
            <CheckCircle className="w-10 h-10 text-stone-200 mx-auto mb-2" />
            <p className="text-sm text-stone-400 mb-3">No tasks assigned</p>
            {onAddTask && (
              <button
                onClick={onAddTask}
                className="text-xs px-4 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors flex items-center gap-1 mx-auto"
              >
                <Plus className="w-3 h-3" />
                Create Task
              </button>
            )}
          </div>
        )}
      </div>
      {tasks.length > 5 && (
        <div className="px-4 py-2 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
          <button 
            onClick={() => router.push('/tasks')}
            className="text-xs text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1"
          >
            View all {tasks.length} tasks
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
